import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useState } from 'react'

import { planningShiftsSelectors, runsheetShiftsSelectors, runsheetsSelectors, scenariosSelectors } from '@/entities/schedules'
import { updateShiftStartTime } from '@/entities/schedules/thunks/updateShiftStartTime'
import { Button, Icon, Modal } from '@/shared/components'
import { DialogComponentProps } from '@/shared/components/Modal/models/DialogStructure'
import { TimePicker } from '@/shared/components/TimePicker/TimePicker'
import { useToastContext } from '@/shared/components/Toast/ToastContext'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { faTruck } from '@fortawesome/pro-solid-svg-icons'
import { Alert, Box, Grid2, styled, Typography } from '@mui/material'

export type UpdateShiftStartTimeDialogProps = DialogComponentProps & {
  onClose: () => void
}

export const UpdateShiftStartTimeDialog: React.FC<UpdateShiftStartTimeDialogProps> = ({
  open,
  onClose,
  shiftId,
  scheduleId,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [newStartTime, setNewStartTime] = useState<Dayjs | null>(null)
  const [showTimeWarning, setShowTimeWarning] = useState(false)
  const { setToastContent } = useToastContext()

  const currentScreen = useAppSelector((state) => state.userInterface.currentScreen)
  const shift = useAppSelector((state) => {
    if (currentScreen === 'planning') {
      return planningShiftsSelectors.selectById(state, shiftId)
    }

    return runsheetShiftsSelectors.selectById(state, shiftId)
  })

  const schedule = useAppSelector((state) => {
    if (currentScreen === 'planning') {
      return scenariosSelectors.selectById(state, scheduleId)
    }

    return runsheetsSelectors.selectById(state, scheduleId)
  })

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (shift?.start?.plannedTime) {
      setNewStartTime(dayjs(shift.start.plannedTime, 'HH:mm'))
    }
  }, [shift])

  // Validate time against workDay constraints
  const validateTime = (time: Dayjs | null) => {
    if (!time || !shift?.workDay) return

    const timeStr = time.format('HH:mm')
    const earliest = shift.workDay.startTimeEarliest
    const latest = shift.workDay.startTimeLatest

    const isOutsideRange = timeStr < earliest || timeStr > latest

    setShowTimeWarning(isOutsideRange)
  }

  // On Change Start Time
  const onChangeStartTime = (time: Dayjs | null) => {
    setNewStartTime(time)
    validateTime(time)
  }

  // On Submit Form
  const onSubmit = async () => {
    if (!newStartTime) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Start Time Required',
        content: 'New start time is required.',
      })

      return
    }

    setIsLoading(true)

    try {
      await dispatch(updateShiftStartTime({
        shiftId,
        scheduleId,
        type: currentScreen,
        startTime: newStartTime.format('HH:mm'),
        isNextDay: false,
      }))

      setToastContent({
        open: true,
        severity: 'success',
        title: 'Success',
        content: 'Shift start time updated successfully.',
      })

      onClose()
    } catch (error) {
      setToastContent({
        open: true,
        severity: 'error',
        title: 'Error',
        content: 'Failed to update shift start time.',
      })
    }

    setIsLoading(false)
  }

  if (!shift || !schedule) return null

  return (
    <Modal
      title={'Update Start Time'}
      onClose={onClose}
      open={open}
      transition="opacity"
      showBackground={true}
      disableClose={isLoading}
    >
      <StyledContent>
        {showTimeWarning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Selected time is outside the recommended time range for this shift's work day.
          </Alert>
        )}
        <Grid2 container spacing={'0.5rem'} display={'inline-flex'} alignItems={'center'} sx={{ mb: 1 }}>
          <Grid2>
            <Typography variant="body2">{schedule.name}</Typography>
          </Grid2>
          <Grid2>
            <Icon icon={faChevronRight} sx={{ fontSize: '1rem' }} />
          </Grid2>
          <Grid2 sx={{ display: 'inline-flex', gap: '0.25rem' }}>
            <Icon icon={faTruck} sx={{ color: shift.uiColor, fontSize: '1rem' }} />
            <Typography variant="body2">{shift.name}</Typography>
          </Grid2>
        </Grid2>
        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Date: {shift.start.plannedDate}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start time window: {shift.workDay.startTimeEarliest} - {shift.workDay.startTimeLatest}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current start time: {shift.start.plannedTime}
          </Typography>
        </Box>

        <TimePicker
          label="New Start Time"
          format="HH:mm"
          views={['hours', 'minutes']}
          onAccept={onChangeStartTime}
          onChange={onChangeStartTime}
          value={newStartTime}
          slotProps={{
            textField: {
              fullWidth: true,
              required: true,
            },
          }}
        />

        <StyledFormActions>
          {onClose && <Button text onClick={onClose}>Cancel</Button>}
          <Button
            primary
            onClick={onSubmit}
            disabled={!newStartTime || isLoading}
          >
            Update
          </Button>
        </StyledFormActions>
      </StyledContent>
    </Modal>
  )
}

const StyledContent = styled('div')`
  ${(props) => props.theme.breakpoints.up('sm')} {
    width: ${pxToRem(460)};
  }
`

const StyledFormActions = styled('div')`
  width: 100%;
  padding-bottom: ${pxToRem(12)};
  display: flex;
  justify-content: flex-end;
  gap: ${pxToRem(12)};
  margin-top: ${pxToRem(24)};
  padding-top: ${pxToRem(8)};
  background-color: ${(props) => props.theme.palette.background.paper};
`